/**
 * The character studio (docs/DESIGN_CHARACTER.md): the overlay in index.html — a turnaround portrait (draw/portrait.ts) and
 * the wardrobe by slot. Picking an owned look wears it at once; picking one for sale previews it on the portrait and offers
 * "Buy & wear" (only inside a clothing shop). Wearing/buying goes through `wear(id)` — the local game or the server.
 */
import type { Registry } from '../content/registry';
import type { Cosmetic } from '../content/types';
import { catalogue, defaultOf, LOOK_SLOTS, owns, resolveLook, SHOP_WHERE, SLOT_NAMES, SLOT_SHOP, type Look, type LookShop, type LookSlot } from './cosmetics';
import { drawPortrait } from '../draw/portrait';

export interface StudioHost {
  reg: Registry;
  look(): Look | undefined;
  wardrobe(): string[];
  cash(): number;
  name(): string;
  /** the shop the player stands in (what it sells can be bought here); undefined anywhere else */
  shop(): LookShop | undefined;
  wear(id: string): void;
}

let host: StudioHost | undefined;
let theta = 0.35, preview: Look | undefined, previewId: string | undefined, dragX: number | undefined, raf = 0;
const el = <T extends HTMLElement>(id: string): T => document.getElementById(id) as T;

export function studioOpen(): boolean { return !el<HTMLDivElement>('studio').hidden; }

export function openStudio(h: StudioHost): void {
  host = h; preview = undefined; previewId = undefined;
  el<HTMLDivElement>('studio').hidden = false;
  const sh = h.shop();
  el<HTMLDivElement>('studionote').textContent = sh === 'barber' ? 'The barber cuts and colours: hair and beards for sale here. The rest of your wardrobe you can switch anytime.' : sh === 'clothing' ? 'Tops, hats and glasses for sale here; hair and beards are cut at a barber shop.' : 'What you own — hair and beards change at a barber shop, clothes at a clothing shop or mall.';
  render();
}
export function closeStudio(): void { el<HTMLDivElement>('studio').hidden = true; host = undefined; preview = undefined; previewId = undefined; cancelAnimationFrame(raf); }

/** the look shown: the real one with the previewed piece on top */
function shown(): Look { const base = { ...(host?.look() ?? {}) }; return preview ? { ...base, ...preview } : base; }

function paint(): void {
  if (!host) return;
  const cv = el<HTMLCanvasElement>('studiocanvas'), ctx = cv.getContext('2d')!;
  drawPortrait(ctx, cv.width, cv.height, resolveLook(host.reg, shown()), theta, { name: host.name() });
}

function render(): void {
  if (!host) return;
  paint();
  el<HTMLDivElement>('studiocash').textContent = `$${host.cash()}`;
  const box = el<HTMLDivElement>('studioslots'); box.innerHTML = '';
  const look = host.look() ?? {}, wd = host.wardrobe();
  const sh = host.shop();
  for (const slot of LOOK_SLOTS) {
    const all = catalogue(host.reg, slot);
    if (!all.length) continue;
    // only what you own (owner: "it should only show things that you have bought") — plus what this shop sells, marked with a price
    const sells = SLOT_SHOP[slot] === 'free' || SLOT_SHOP[slot] === sh;
    const items = all.filter((c) => owns(wd, c) || sells);
    const h = document.createElement('div'); h.style.cssText = 'margin:8px 0 4px;font-weight:bold;color:#c8d4e0';
    const cur = look[slot] ?? defaultOf(host.reg, slot)?.id;
    const missing = all.length - items.length;
    h.innerHTML = `${SLOT_NAMES[slot]}${cur ? ` — ${host.reg.get('cosmetic', cur)?.name ?? ''}` : ''}${missing ? ` <span style="font-weight:normal;color:#8a97a8">· ${missing} more at ${SHOP_WHERE[SLOT_SHOP[slot]]}</span>` : ''}`;
    box.append(h);
    const row = document.createElement('div'); row.style.cssText = 'display:flex;flex-wrap:wrap;gap:6px';
    for (const c of items) row.append(chip(c, slot, c.id === cur, owns(wd, c), sells));
    box.append(row);
  }
  const buy = el<HTMLButtonElement>('studiobuy');
  if (previewId && host) { const c = host.reg.get('cosmetic', previewId)!; buy.hidden = false; buy.textContent = `${SLOT_SHOP[c.slot as LookSlot] === 'barber' ? 'Cut & pay' : 'Buy & wear'} ${c.name} — $${c.price}`; buy.disabled = host.cash() < c.price; buy.style.opacity = buy.disabled ? '0.5' : '1'; }
  else buy.hidden = true;
}

function chip(c: Cosmetic, slot: LookSlot, wearing: boolean, owned: boolean, sells: boolean): HTMLButtonElement {
  const b = document.createElement('button');
  const swatch = c.color && c.color !== '#000000' && slot !== 'body' ? `<span style="display:inline-block;width:12px;height:12px;border-radius:3px;background:${c.color};vertical-align:-2px;margin-right:5px;border:1px solid rgba(255,255,255,0.25)"></span>` : '';
  b.innerHTML = `${swatch}${c.name}${wearing ? ' ✓' : owned ? '' : ` <span style="color:#ffd24a">$${c.price}</span>`}`;
  b.style.cssText = `padding:5px 9px;border-radius:6px;font-size:13px;cursor:pointer;border:1px solid ${wearing ? '#6fb6ff' : previewId === c.id ? '#ffd24a' : '#3a4a5c'};background:${wearing ? '#26415c' : '#202a36'};color:#e8f0ff`;
  b.onclick = () => {
    if (!host) return;
    if (owned || c.price === 0) { preview = undefined; previewId = undefined; host.wear(c.id); setTimeout(render, 60); setTimeout(render, 400); }
    else if (sells) { preview = { [slot]: c.id }; previewId = c.id; render(); }
  };
  return b;
}

/** hook the overlay's controls once */
export function initStudio(): void {
  const cv = el<HTMLCanvasElement>('studiocanvas');
  const turn = (d: number) => { theta = (theta + d + Math.PI * 2) % (Math.PI * 2); paint(); };
  el<HTMLButtonElement>('studioleft').onclick = () => turn(-Math.PI / 4);
  el<HTMLButtonElement>('studioright').onclick = () => turn(Math.PI / 4);
  el<HTMLButtonElement>('studioclose').onclick = () => closeStudio();
  el<HTMLButtonElement>('studiobuy').onclick = () => { if (host && previewId) { host.wear(previewId); preview = undefined; previewId = undefined; setTimeout(render, 60); setTimeout(render, 400); } };
  cv.addEventListener('pointerdown', (e) => { dragX = e.clientX; cv.setPointerCapture(e.pointerId); });
  cv.addEventListener('pointermove', (e) => { if (dragX === undefined) return; turn((e.clientX - dragX) / 60); dragX = e.clientX; });
  cv.addEventListener('pointerup', () => { dragX = undefined; }); cv.addEventListener('pointercancel', () => { dragX = undefined; });
  // a slow idle turn while nobody drags
  const spin = () => { if (studioOpen() && dragX === undefined) { theta = (theta + 0.004) % (Math.PI * 2); paint(); } raf = requestAnimationFrame(spin); };
  raf = requestAnimationFrame(spin);
}
